import { Injectable, inject } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { ThemeService } from '../services/theme.service';
import { TranslationService } from '../i18n/translation.service';

/** Only routes that can show a Chart.js chart start the (large) chart library early; every other page never loads it. */
const CHART_ROUTES = /report|dashboard|tallysheet|tally-sheet|inventory|stock|command-center|analytics|admin|affiliate|crm/i;

/**
 * One look for every Chart.js chart (2026-10-08): soft grid, muted labels, rounded bars, smooth lines, glass tooltips, doughnut ring.
 * Sets Chart.js DEFAULTS only, so a chart that sets its own colour or option keeps it. Colours follow Day / Night mode.
 * The chart library is loaded with the same dynamic import PrimeNG uses, so it is the same file, fetched a little earlier.
 */
@Injectable({ providedIn: 'root' })
export class ChartThemeService {
  private readonly theme = inject(ThemeService);
  private readonly i18n = inject(TranslationService);
  private chartLib: Promise<any> | null = null;
  private emptyRegistered = false;

  constructor() {
    inject(Router).events.subscribe(e => {
      if (e instanceof NavigationStart && CHART_ROUTES.test(e.url)) this.ensure();
    });
    // Night / Day switch: re-apply once the library is there (charts drawn afterwards use the new colours)
    this.theme.isDark$.subscribe(() => { if (this.chartLib) this.ensure(); });
  }

  ensure(): Promise<void> {
    if (!this.chartLib) this.chartLib = import('chart.js/auto').then(m => m.Chart);
    return this.chartLib.then(Chart => this.apply(Chart)).catch(() => undefined);
  }

  private apply(Chart: any): void {
    const dark = this.theme.isDark;
    const d = Chart.defaults;
    const body = typeof document !== 'undefined' ? getComputedStyle(document.body).fontFamily : '';
    if (body) d.font.family = body;
    d.font.size = 12;
    d.color = dark ? '#a9bcc6' : '#5d7684';
    d.borderColor = dark ? 'rgba(255,255,255,0.08)' : 'rgba(15,42,55,0.07)';
    // edit the existing settings object: replacing it drops Chart.js's own scripting flags and the chart then fails to draw
    if (d.animation) { d.animation.duration = 600; d.animation.easing = 'easeOutQuart'; }
    if (d.scale?.grid) { d.scale.grid.drawTicks = false; }
    if (d.scale?.border) { d.scale.border.display = false; }
    if (d.elements?.bar) { d.elements.bar.borderRadius = 8; d.elements.bar.borderSkipped = false; }
    if (d.elements?.line) { d.elements.line.tension = 0.35; d.elements.line.borderWidth = 2.5; }
    if (d.elements?.point) { d.elements.point.hoverRadius = 6; }
    const tip = d.plugins?.tooltip;
    if (tip) {
      tip.backgroundColor = dark ? 'rgba(8,22,30,0.96)' : 'rgba(15,42,55,0.94)';
      tip.titleColor = '#ffffff';
      tip.bodyColor = 'rgba(255,255,255,0.88)';
      tip.cornerRadius = 12;
      tip.padding = 12;
      tip.boxPadding = 5;
      tip.usePointStyle = true;
      tip.caretSize = 6;
      if (tip.titleFont) tip.titleFont.weight = '700';
    }
    const legend = d.plugins?.legend;
    if (legend?.labels) { legend.labels.usePointStyle = true; legend.labels.boxWidth = 8; legend.labels.boxHeight = 8; }
    if (Chart.overrides?.doughnut) Chart.overrides.doughnut.cutout = '66%';
    this.registerEmptyState(Chart);
  }

  /**
   * A chart with nothing to draw (no data, or only zeros) shows a small picture and "No data yet" instead of a blank box (2026-10-08).
   * One plugin for every Chart.js chart in the app; it paints over the empty axes, so a chart with data is never touched.
   */
  private registerEmptyState(Chart: any): void {
    if (this.emptyRegistered) return;
    this.emptyRegistered = true;
    const isEmpty = (chart: any): boolean => {
      const sets: any[] = chart.data?.datasets ?? [];
      if (!sets.length) return true;
      return sets.every(ds => {
        const data: any[] = ds?.data ?? [];
        return !data.some(v => {
          if (v === null || v === undefined) return false;
          if (typeof v === 'object') return true; // bubble / scatter points: present = data
          const n = Number(v);
          return Number.isFinite(n) && n !== 0;
        });
      });
    };
    const backgroundOf = (el: HTMLElement | null): string => {
      for (let n: HTMLElement | null = el; n; n = n.parentElement) {
        const c = getComputedStyle(n).backgroundColor;
        if (c && c !== 'transparent' && !/rgba\(\s*\d+,\s*\d+,\s*\d+,\s*0\s*\)/.test(c)) return c;
      }
      return this.theme.isDark ? '#1e2630' : '#ffffff';
    };
    Chart.register({
      id: 'llEmptyState',
      afterDraw: (chart: any) => {
        if (!chart.width || !chart.height || !isEmpty(chart)) return;
        const ctx = chart.ctx;
        const dark = this.theme.isDark;
        const s = Math.max(0.6, Math.min(1, Math.min(chart.width, chart.height) / 220));
        const cx = chart.width / 2;
        const cy = chart.height / 2;
        ctx.save();
        ctx.fillStyle = backgroundOf(chart.canvas);
        ctx.fillRect(0, 0, chart.width, chart.height);
        // three soft bars
        const bw = 12 * s, gap = 7 * s, base = cy - 4 * s;
        const heights = [20 * s, 34 * s, 25 * s];
        ctx.fillStyle = dark ? 'rgba(92,200,230,0.38)' : 'rgba(22,132,172,0.28)';
        heights.forEach((h, i) => {
          const x = cx - (3 * bw + 2 * gap) / 2 + i * (bw + gap);
          if (typeof ctx.roundRect === 'function') { ctx.beginPath(); ctx.roundRect(x, base - h, bw, h, 4 * s); ctx.fill(); }
          else ctx.fillRect(x, base - h, bw, h);
        });
        const family = Chart.defaults.font?.family || 'sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'alphabetic';
        ctx.fillStyle = dark ? '#c5d4dc' : '#31515f';
        ctx.font = '700 ' + Math.round(13 * Math.max(0.9, s)) + 'px ' + family;
        ctx.fillText(this.i18n.instant('chart.emptyTitle'), cx, base + 24 * s, chart.width - 24);
        ctx.fillStyle = dark ? '#8fa6b2' : '#6b8794';
        ctx.font = '500 ' + Math.round(11.5 * Math.max(0.9, s)) + 'px ' + family;
        ctx.fillText(this.i18n.instant('chart.emptyHint'), cx, base + 42 * s, chart.width - 24);
        ctx.restore();
      }
    });
  }
}
